import { useState, type FormEvent, type JSX } from 'react';
import { usePostHog } from '@posthog/react';
import { useLocation } from 'react-router';
import { Button, Card, Field, Textarea } from '@viniciomendesr/rayrovisk-design-system';
import { useSubmitFeedback, type FeedbackTopic } from '../api/queries';

const FEEDBACK_TOPICS = [
  { value: 'blocked', label: 'Algo atrapalhou' },
  { value: 'idea', label: 'Tenho uma ideia' },
  { value: 'worked', label: 'Funcionou bem' }
] as const;

/**
 * Product feedback is durable product data. PostHog receives only its shape,
 * never the sentence she wrote.
 */
export function FeedbackScreen(): JSX.Element {
  const posthog = usePostHog();
  const location = useLocation();
  const submitFeedback = useSubmitFeedback();
  const [topic, setTopic] = useState<FeedbackTopic>(FEEDBACK_TOPICS[0].value);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const feedback = message.trim();
    if (feedback === '') {
      return;
    }

    try {
      await submitFeedback.mutateAsync({ topic, message: feedback });
      posthog?.capture('feedback_submitted', {
        topic,
        message_length: feedback.length,
        surface: 'feedback_screen',
        path: location.pathname
      });
      setSubmitted(true);
    } catch {
      // The mutation exposes its error below. Success stays tied to the durable
      // write rather than to the button click.
    }
  }

  function writeAnother() {
    setMessage('');
    setSubmitted(false);
    submitFeedback.reset();
  }

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        width: '100%',
        maxWidth: 516,
        margin: '-6px 0 0 -4px'
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, font: '400 28px/1.12 var(--rvk-font-expressive)' }}>
          Deixar uma opinião
        </h1>
        <p
          style={{
            margin: 0,
            maxWidth: 510,
            font: '400 13.5px/1.55 var(--rvk-font-functional)',
            color: 'var(--rvk-text-secondary)'
          }}
        >
          Escreva do jeito que você falaria. Quem lê é quem mexe no produto e decide o que entra
          primeiro.
        </p>
      </header>

      <Card layer="functional" padding="md">
        {submitted ? (
          <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ font: '400 20px/1.25 var(--rvk-font-expressive)' }}>
                A sua opinião chegou.
              </span>
              <p
                style={{
                  margin: 0,
                  font: '400 13.5px/1.55 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Obrigado por ajudar a decidir o que merece atenção primeiro.
              </p>
            </div>
            <div>
              <Button type="button" variant="secondary" onClick={writeAnother}>
                Enviar outra opinião
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              padding: '0 7px 3px'
            }}
          >
            <fieldset
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                margin: 0,
                padding: 0,
                border: 0
              }}
            >
              <legend
                style={{ padding: 0, font: '500 12.5px/1.4 var(--rvk-font-functional)' }}
              >
                Sobre o que é
              </legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {FEEDBACK_TOPICS.map((option) => {
                  const selected = topic === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className="rvk-focus-ring"
                      aria-pressed={selected}
                      onClick={() => setTopic(option.value)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: 30,
                        padding: '5px 8px',
                        cursor: 'pointer',
                        background: selected
                          ? 'var(--rvk-accent-subtle)'
                          : 'var(--rvk-surface-1)',
                        color: 'var(--rvk-text-primary)',
                        border: `1px solid ${
                          selected
                            ? 'var(--rvk-state-selected-ring)'
                            : 'var(--rvk-border-subtle)'
                        }`,
                        borderRadius: 'var(--rvk-radius-full)',
                        font: `${selected ? 500 : 400} 11px/1.3 var(--rvk-font-functional)`
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <Field
              label="O que você quer dizer"
              hint="Se foi em uma tela específica, diga qual. Ajuda a achar rápido."
            >
              <Textarea
                required
                rows={4}
                maxLength={1200}
                placeholder="ex.: o roteiro saiu bom, mas eu perdi o crédito antes de conseguir copiar"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </Field>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              {submitFeedback.isError && (
                <p
                  role="alert"
                  style={{
                    margin: 0,
                    font: '400 12.5px/1.45 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-primary)'
                  }}
                >
                  {(submitFeedback.error as Error).message}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={submitFeedback.isPending}
                style={{
                  minHeight: 34,
                  height: 34,
                  padding: '7px 12px',
                  font: '600 12.5px/1 var(--rvk-font-functional)'
                }}
              >
                Enviar a minha opinião
              </Button>
              <span
                style={{
                  font: '400 12.5px/1.45 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Prefere e-mail? Escreva para{' '}
                <a
                  href="mailto:acesso@roteirovisk.com?subject=Opini%C3%A3o%20sobre%20o%20Roteirovisk"
                  style={{ color: 'var(--rvk-accent-active)' }}
                >
                  acesso@roteirovisk.com
                </a>
                .
              </span>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
